/**
 * Comparison Selection Store
 *
 * Manages profile selection for side-by-side comparison.
 * Supports selecting up to 3 profiles and toggling selection mode.
 */

import { create } from 'zustand';

interface ComparisonSelectionState {
  /** List of selected profile IDs (max 3) */
  selectedProfileIds: string[];

  /** Whether selection mode is active */
  isSelectionMode: boolean;

  /** Toggle a profile in/out of selection */
  toggleProfile: (profileId: string) => void;

  /** Clear all selections */
  clearSelection: () => void;

  /** Set selection mode on/off */
  setSelectionMode: (enabled: boolean) => void;
}

export const useComparisonSelectionStore = create<ComparisonSelectionState>((set) => ({
  selectedProfileIds: [],
  isSelectionMode: false,

  toggleProfile: (profileId) => {
    set((state) => {
      const current = state.selectedProfileIds;

      // If already selected, deselect
      if (current.includes(profileId)) {
        return {
          selectedProfileIds: current.filter((id) => id !== profileId),
        };
      }

      // If at max (3), don't allow more selections
      if (current.length >= 3) {
        return state;
      }

      // Add to selection
      return {
        selectedProfileIds: [...current, profileId],
      };
    });
  },

  clearSelection: () => {
    set({
      selectedProfileIds: [],
      isSelectionMode: false,
    });
  },

  setSelectionMode: (enabled) => {
    set(() => ({
      isSelectionMode: enabled,
    }));
  },
}));
